import type {
  AjaxResponseOfGetCurrentLoginInformationsOutput,
  GetCurrentLoginInformationsOutput,
} from "@repo/sync-utils/sync/api";

export type CanonicalSyncSessionSnapshot = GetCurrentLoginInformationsOutput & {
  hydrated: boolean;
};

type SyncSessionReader = {
  getCurrentLoginInformations(input: {
    currentTeamId?: number;
  }): PromiseLike<AjaxResponseOfGetCurrentLoginInformationsOutput>;
};

type SyncSessionPersistence = (
  session: GetCurrentLoginInformationsOutput,
) => void;

type SyncSessionPublisher = (
  session: GetCurrentLoginInformationsOutput,
) => unknown;

export type SyncSessionStorageKeys = {
  grantedPermissions: string;
  user: string;
  userApps: string;
  userPermission: string;
};

type RemovableSyncSessionStorage = {
  removeItem(key: string): void;
};

type VersionedSyncSessionStorage = RemovableSyncSessionStorage & {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

type SyncSessionStorageEvent = {
  key: string | null;
  newValue?: string | null;
  storageArea?: unknown;
};

type SyncSessionStorageEventSource = {
  addEventListener(
    type: "storage",
    listener: (event: SyncSessionStorageEvent) => void,
  ): void;
  removeEventListener(
    type: "storage",
    listener: (event: SyncSessionStorageEvent) => void,
  ): void;
};

const EMPTY_SYNC_SESSION: CanonicalSyncSessionSnapshot = {
  apps: null,
  grantedPermissions: null,
  hydrated: false,
};

let canonicalSyncSession = EMPTY_SYNC_SESSION;
const listeners = new Set<() => void>();

export function getCanonicalSyncSessionSnapshot() {
  return canonicalSyncSession;
}

export function getServerSyncSessionSnapshot() {
  return EMPTY_SYNC_SESSION;
}

export function subscribeCanonicalSyncSession(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function replaceCanonicalSyncSession(
  session: GetCurrentLoginInformationsOutput,
) {
  canonicalSyncSession = { ...session, hydrated: true };
  listeners.forEach((listener) => listener());
  return canonicalSyncSession;
}

export function persistAndPublishCanonicalSyncSession(
  session: GetCurrentLoginInformationsOutput,
  persist: SyncSessionPersistence,
) {
  persist(session);
  return replaceCanonicalSyncSession(session);
}

export function resetCanonicalSyncSession() {
  canonicalSyncSession = EMPTY_SYNC_SESSION;
  listeners.forEach((listener) => listener());
}

export function clearAndResetCanonicalSyncSession(
  storage: RemovableSyncSessionStorage,
  storageKeys: SyncSessionStorageKeys,
) {
  storage.removeItem(storageKeys.user);
  storage.removeItem(storageKeys.userPermission);
  storage.removeItem(storageKeys.userApps);
  storage.removeItem(storageKeys.grantedPermissions);
  resetCanonicalSyncSession();
}

export function migratePersistedSyncSessionStorage(
  storage: VersionedSyncSessionStorage,
  storageKeys: SyncSessionStorageKeys,
  versionKey: string,
  currentVersion: string,
) {
  if (storage.getItem(versionKey) === currentVersion) return true;

  storage.removeItem(storageKeys.user);
  storage.removeItem(storageKeys.userPermission);
  storage.removeItem(storageKeys.userApps);
  storage.removeItem(storageKeys.grantedPermissions);
  storage.setItem(versionKey, currentVersion);
  return false;
}

export function subscribeCanonicalSyncSessionStorageEvents({
  canHydrate = () => true,
  eventSource,
  expectedStorage,
  onExternalReset = () => {},
  onTombstoneChange = () => {},
  readPersisted,
  storageKeys,
  tombstoneKey,
}: {
  canHydrate?: () => boolean;
  eventSource: SyncSessionStorageEventSource;
  expectedStorage?: unknown;
  onExternalReset?: () => void;
  onTombstoneChange?: (blocked: boolean) => void;
  readPersisted: () => GetCurrentLoginInformationsOutput;
  storageKeys: readonly string[];
  tombstoneKey?: string;
}) {
  const handleStorage = (event: SyncSessionStorageEvent) => {
    if (
      event.storageArea &&
      expectedStorage &&
      event.storageArea !== expectedStorage
    ) {
      return;
    }

    if (tombstoneKey && event.key === tombstoneKey) {
      if (event.newValue !== null) {
        onTombstoneChange(true);
        resetCanonicalSyncSession();
      }
      return;
    }

    if (event.key !== null && !storageKeys.includes(event.key)) {
      return;
    }

    try {
      const session = readPersisted();
      if (hasPersistedSyncSessionValue(session) && canHydrate()) {
        replaceCanonicalSyncSession(session);
      } else {
        if (!hasPersistedSyncSessionValue(session)) onExternalReset();
        resetCanonicalSyncSession();
      }
    } catch {
      onExternalReset();
      resetCanonicalSyncSession();
    }
  };

  eventSource.addEventListener("storage", handleStorage);
  return () => eventSource.removeEventListener("storage", handleStorage);
}

export async function refreshCanonicalSyncSession(
  sessionService: SyncSessionReader,
  currentTeamId: number | undefined,
  publish: SyncSessionPublisher,
) {
  const response = await sessionService.getCurrentLoginInformations({
    currentTeamId,
  });
  if (!response.result) throw new Error("sync_session_refresh_failed");

  publish(response.result);
  return response.result;
}

export function isCanonicalSyncSessionComplete(
  session: CanonicalSyncSessionSnapshot,
) {
  return Boolean(
    session.hydrated &&
      session.user &&
      session.permissions &&
      Array.isArray(session.apps) &&
      Array.isArray(session.grantedPermissions),
  );
}

export function shouldRefreshSyncSession({
  asPath,
  currentTeamId,
  hasCompleteSession,
  hasSyncToken,
  isStorageHydrated,
  revalidatedTeamId,
}: {
  asPath: string;
  currentTeamId?: string;
  hasCompleteSession: boolean;
  hasSyncToken: boolean;
  isStorageHydrated: boolean;
  revalidatedTeamId: string | null;
}) {
  if (!isStorageHydrated || !hasSyncToken) return false;
  if (!hasCompleteSession) return true;
  return (
    asPath.split(/[?#]/)[0] === "/apps" &&
    Boolean(currentTeamId) &&
    revalidatedTeamId !== currentTeamId
  );
}

function hasPersistedSyncSessionValue(
  session: GetCurrentLoginInformationsOutput,
) {
  return Boolean(
    session.user ||
      session.permissions ||
      session.apps ||
      session.grantedPermissions,
  );
}
